import dayjs from "dayjs";
import api from "./InterfaceApi";
import IfaceSyncInterface from "../models/IfaceSyncInterface";
import IfaceSyncInterfaceType from "../models/IfaceSyncInterfaceType";
import IfaceSyncFeature, { IfaceFeature } from "../models/IfaceSyncFeature";
import { IfaceType } from "../models/IfaceType";
import { Iface } from "../models/Iface";
import IfaceSyncStatus, { SyncStatus } from "../models/IfaceSyncStatus";
import IfaceSyncPartnerDTO, { IfaceSyncPartnerDTODefaultProps } from "../models/IfaceSyncPartnerDTO";
import IfaceSyncInvoiceHeadDTO, { IfaceSyncInvoiceHeadDTODefaultProps } from "../models/IfaceSyncInvoiceHeadDTO";
import GeneralGetPurchaseInvoicesRequestDTO from "../models/GeneralGetPurchaseInvoicesRequestDTO";
import GeneralGetCarsRequestDTO from "../models/GeneralGetCarsRequestDTO";
import GeneralGetPartnersRequestDTO from "../models/GeneralGetPartnersRequestDTO";
import IfaceSyncDictionaryOption from "../models/IfaceSyncDictionaryOption";
import globalStore from "@/models/GlobalStore";
import { t } from "@/plugins/i18n";

export class InterfaceStore {

  showRequestTool = false;

  /**
   * A részletes szerkesztő csak megtekintésre nyílt meg (az Előzmények menüpontból). Ekkor minden
   * mező tiltott és a szinkron műveletek sem érhetők el — a szerkesztő nézegetőként viselkedik.
   */
  editorViewOnly = false;

  /** Élő interfészek (cssysisynciface) – az interfész picker forrása */
  interfaces: IfaceSyncInterface[] = [];

  /** Aktuálisan kiválasztott interfész */
  selectedInterface = 1;

  /** Élő interfész típusok (cssysisyncifacetyp) – az interfész típus picker forrása */
  visibleInterfaceTypes: IfaceSyncInterfaceType[] = [];

  /** Aktuálisan kiválasztott interfész típus */
  selectedInterfaceType = 1;

  /** A kiválasztott interfész típus láthatóságai és engedélyezett funkciói (rssysisyncfeature) */
  features: IfaceSyncFeature[] = [];

  /** Élő szinkron státuszok (sssysisyncstatus) — a multiselect szűrő forrása */
  syncStatuses: IfaceSyncStatus[] = [];

  /** A szerkesztett rekord interfészének fizetési mód mappelései (a részletes szerkesztő választója) */
  payModeOptions: IfaceSyncDictionaryOption[] = [];

  /** A szerkesztett rekord interfészének áfakulcs mappelései (a tétel szerkesztő választója) */
  taxOptions: IfaceSyncDictionaryOption[] = [];

  /** K2D pénznemek (a részletes szerkesztő pénznem választója) */
  currencies: string[] = [];

  // --- Toolbar globális szűrők — mindkét apisearchre hatnak ---

  /** Globális keresési szöveg */
  globalSearchText = "";

  /** Státusz checkboxok — default: minden be, kivéve Kihagyva */
  statusInProgressOn = true;
  statusDoneOn = true;
  statusErrorOn = true;
  statusSkipOn = true;

  /** Dátum szűrő — tól: mai nap -7 nap, ig: mai nap */
  fromDate: Date = dayjs().subtract(1, "month").startOf("day").toDate();
  toDate: Date = new Date();

  // --- Aktuálisan látható apisearch tételek (InterfaceView tölti az ApiSearchPage @items eseményén keresztül) ---
  /** A sikeres lista pillanatnyi tételei (SyncListApiSearch) */
  syncListItems: any[] = [];
  /** A hibás lista pillanatnyi tételei (SyncErrorListApiSearch) */
  errorListItems: any[] = [];

  disabledTooltip = t("Interface_AMezoErtekeNemModosithatoMert");

  /**
   * Mindkét apisearch (sikeres + hibás) `selected === true` sorainak teljes DTO-ja.
   * A toolbar "Kijelölt tételek szinkronizálása" gombja ezt küldi a backendre — onnan
   * a backend dönti el (UserModified flag alapján), hogy új sort hoz létre a köztes
   * táblába (módosított sor) vagy az ID-t továbbadja az IfaceSync API-nak (nem módosított).
   */
  get selectedItems(): any[] {
    return [...this.syncListItems, ...this.errorListItems].filter((i: any) => i?.selected === true);
  }

  /**
   * Verk interfész + kimenő számla típus: a lista nem a közös köztes táblából, hanem a Verk saját
   * háromszintű köztes tábláiból (hmifcapiverkinv → hmifcapiverkacc → imifcapiverkacc) épül.
   */
  get isVerkOutInvoice(): boolean {
    return this.selectedInterface === Iface.Verk && this.selectedInterfaceType === IfaceType.OutInvoice;
  }

  /** Megasoft számla (kimenő vagy bejövő) interfész-típus van kiválasztva. */
  get isMegasoftInvoice(): boolean {
    return this.selectedInterface === Iface.Megasoft
      && (this.selectedInterfaceType === IfaceType.OutInvoice || this.selectedInterfaceType === IfaceType.InInvoice);
  }

  async loadInterfaces(): Promise<void> {
    this.interfaces = await api.getLiveInterfaces();
    if (this.selectedInterface === 1 && this.interfaces.length > 0) {
      this.selectedInterface = this.interfaces[0].id;
    }
  }

  async loadInterfaceTypes(): Promise<void> {
    const interfaceTypes: IfaceSyncInterfaceType[] = await api.getLiveInterfaceTypes() ?? [];
    this.visibleInterfaceTypes = interfaceTypes.filter((t) => this.features.some((f) => f.interface === this.selectedInterface && f.interfaceType === t.id && f.visible));
    if (this.visibleInterfaceTypes.length > 0 && !this.visibleInterfaceTypes.some((t) => t.id === this.selectedInterfaceType)) {
      this.selectedInterfaceType = this.visibleInterfaceTypes[0].id;
    }
  }

  async loadSyncStatuses(): Promise<void> {
    this.syncStatuses = await api.getLiveSyncStatuses();
  }

  /** A kiválasztott interfész feature sorainak betöltése */
  async loadFeatures(): Promise<void> {
    this.features = await api.getLiveFeatures();
  }

  /** A megadott funkció használható-e a kiválasztott interfészen és típuson? */
  hasFeature(feature: IfaceFeature): boolean {
    return this.features.find((f) => f.interface === this.selectedInterface && f.interfaceType === this.selectedInterfaceType)?.[feature] ?? false;
  }

  // --- Részletes Partner szerkesztő (SyncEntityEditor view, partner mód) ---

  /** Az aktuálisan szerkesztett partner rekord (a részletes szerkesztőben) */
  editingPartner: IfaceSyncPartnerDTO = { ...IfaceSyncPartnerDTODefaultProps };

  /**
   * A szerkesztett rekord a szinkron sor (interfész + külső azonosító) legutolsó köztes
   * tábla sora-e. A részletes szerkesztőben csak a legutolsó sor módosítható — ha nem az,
   * minden mező és művelet tiltva van.
   */
  editingPartnerIsLatest = true;

  /**
   * A szerkesztett szinkron sor (interfész + külső azonosító) összes köztes tábla sora,
   * újabb → régebbi (csökkenő id) sorrendben. A részletes szerkesztő léptetőjének forrása:
   * az index 0 a legutolsó (legfrissebb), az utolsó elem a legelső (legrégebbi) állapot.
   */
  editingPartnerHistory: IfaceSyncPartnerDTO[] = [];

  /** A szerkesztett rekord indexe a history listában (0 = legutolsó, -1 = nincs a listában). */
  get editingPartnerIndex(): number {
    return this.editingPartnerHistory.findIndex((h) => h.id === this.editingPartner.id);
  }

  /** A szerkesztett rekord sorszáma a history-ban (1 = legelső/legrégebbi, hossz = legutolsó). */
  get editingPartnerPosition(): number {
    return this.editingPartnerIndex < 0 ? 0 : this.editingPartnerHistory.length - this.editingPartnerIndex;
  }

  /** A legutolsó sikeres (Done) köztes tábla sor a history-ból (csökkenő id — az első találat). */
  get editingPartnerLastDone(): IfaceSyncPartnerDTO | undefined {
    return this.editingPartnerHistory.find((h) => h.syncStatus === SyncStatus.Done);
  }

  /** Betölti a megadott id-jú partnert a részletes szerkesztő számára */
  async loadEditingPartner(id: number): Promise<void> {
    if (id <= 1) {
      this.editingPartner = { ...IfaceSyncPartnerDTODefaultProps };
      this.editingPartnerHistory = [];
      this.editingPartnerIsLatest = true;
      return;
    }
    const p = await api.getPartnerById(id);
    this.editingPartner = p ?? { ...IfaceSyncPartnerDTODefaultProps };
    await this.loadEditingPartnerHistory();
  }

  /**
   * Betölti a szerkesztett rekord szinkron sorának összes köztes tábla sorát (interfész +
   * külső azonosító), csökkenő id szerint rendezve, és eldönti, hogy a szerkesztett rekord-e
   * a legutolsó (legnagyobb azonosítójú) sor.
   */
  private async loadEditingPartnerHistory(): Promise<void> {
    this.editingPartnerHistory = [];
    this.editingPartnerIsLatest = true;
    const p = this.editingPartner;
    if (p.id <= 1) {
      return;
    }
    const history = await this.fetchEditingPartnerHistory();
    if (history == null) {
      return;
    }
    this.editingPartnerHistory = [...history].sort((a, b) => b.id - a.id);
    this.editingPartnerIsLatest = history.every((h) => h.id <= p.id);
  }

  /**
   * A szerkesztett partner szinkron előzménye a sor kulcsa szerint: Verk-nél (felfelé irány, a külső kód a
   * felküldésig üres) a belső partner azonosító (mlgenpartner.szam), más interfésznél a külső kód.
   * Null, ha a sornak nincs használható kulcsa.
   */
  private async fetchEditingPartnerHistory(): Promise<IfaceSyncPartnerDTO[] | null> {
    const p = this.editingPartner;
    const iface = this.editingPartnerIface;
    if (iface === Iface.Verk && p.partner > 1) {
      return (await api.getPartnerHistoryByPartner(iface, p.partner)) ?? [];
    }
    if (!p.extCode) {
      return null;
    }
    return (await api.getPartnerHistory(iface, p.extCode)) ?? [];
  }

  // --- Részletes szerkesztő action-ok (partner) ---

  /** A szerkesztett partner interfésze — a DTO-ból, ha nincs, a kiválasztott interfész. */
  private get editingPartnerIface(): number {
    return this.editingPartner.interface > 1 ? this.editingPartner.interface : this.selectedInterface;
  }

  /**
   * A szinkron sor legutolsó köztes tábla sorának betöltése a részletes szerkesztőbe. Újraszinkronizálás
   * és kihagyás után ez tölti be a művelettel létrejött új sort, így a felhasználó a szerkesztőben marad.
   */
  async loadEditingPartnerLatest(): Promise<void> {
    const history = await this.fetchEditingPartnerHistory();
    if (history == null) return;

    const latest = [...history].sort((a, b) => b.id - a.id)[0];
    if (latest) {
      await this.loadEditingPartner(latest.id);
    }
  }

  /**
   * A szerkesztett partner újraszinkronizálása — a backend a DTO-ból új köztes tábla sort
   * hoz létre, majd az új sor azonosítójával meghívja az IfaceSync API-t.
   */
  async downSyncEditingPartner(): Promise<void> {
    await api.downSyncEditedPartner(this.editingPartner, this.editingPartnerIface);
  }

  /**
   * A szerkesztett partner kihagyása a szinkron folyamatból — ugyanaz a backend végpont,
   * mint a kezdőfelület „Kihagyva" gombjánál, egyetlen tétellel.
   */
  async markEditingPartnerSkipped(): Promise<void> {
    await api.markSelectedItemsSkipped([this.editingPartner], this.editingPartnerIface, IfaceType.Partner);
  }

  /**
   * A szerkesztett partner szinkron megjegyzésének mentése — csak a sync_remark és az
   * upddate/updtime/upduser mezők módosulnak, a usermodified flag NEM változik.
   */
  async saveEditingPartnerRemark(): Promise<void> {
    await api.setSyncRemarkOnSelectedItems([this.editingPartner.id], this.editingPartner.syncRemark ?? "", IfaceType.Partner);
  }

  // --- Részletes Számla szerkesztő (SyncEntityEditor view, invoice mód) ---

  /** Az aktuálisan szerkesztett számlafej rekord (a részletes szerkesztőben) */
  editingInvoice: IfaceSyncInvoiceHeadDTO = { ...IfaceSyncInvoiceHeadDTODefaultProps };

  /**
   * A szerkesztett rekord a szinkron sor (interfész + külső azonosító) legutolsó köztes
   * tábla sora-e. A részletes szerkesztőben csak a legutolsó sor módosítható — ha nem az,
   * minden mező és művelet tiltva van.
   */
  editingInvoiceIsLatest = true;

  /**
   * A szerkesztett szinkron sor (interfész + külső azonosító) összes köztes tábla sora,
   * újabb → régebbi (csökkenő id) sorrendben. A részletes szerkesztő léptetőjének forrása:
   * az index 0 a legutolsó (legfrissebb), az utolsó elem a legelső (legrégebbi) állapot.
   */
  editingInvoiceHistory: IfaceSyncInvoiceHeadDTO[] = [];

  /** A szerkesztett rekord indexe a history listában (0 = legutolsó, -1 = nincs a listában). */
  get editingInvoiceIndex(): number {
    return this.editingInvoiceHistory.findIndex((h) => h.id === this.editingInvoice.id);
  }

  /** A szerkesztett rekord sorszáma a history-ban (1 = legelső/legrégebbi, hossz = legutolsó). */
  get editingInvoicePosition(): number {
    return this.editingInvoiceIndex < 0 ? 0 : this.editingInvoiceHistory.length - this.editingInvoiceIndex;
  }

  /** A legutolsó sikeres (Done) köztes tábla sor a history-ból (csökkenő id — az első találat). */
  get editingInvoiceLastDone(): IfaceSyncInvoiceHeadDTO | undefined {
    return this.editingInvoiceHistory.find((h) => h.syncStatus === SyncStatus.Done);
  }

  /**
   * A szinkron sorhoz tartozik beemelt bejövő számla, ami nem hibás rögzítés státuszú. Ilyenkor a
   * számlafej és a tételei nem módosíthatók többé, és nem is szinkronizálhatók újra — akkor sem, ha
   * egy későbbi újraszinkronizálás miatt Kihagyva státusszal újabb köztes tábla sor keletkezett.
   * A teljes szinkron soron vizsgáljuk, mert a beemelés bármelyik korábbi soron történhetett.
   */
  get editingInvoiceIsImported(): boolean {
    return this.editingInvoiceHistory.some((h) => h.isImported);
  }

  /**
   * A szinkron sorhoz tartozik hibás rögzítésre állított bejövő számla. Ez nem zárja le a rekordot —
   * a felületen csak tájékoztatunk róla, hogy a felhasználó tudja, miért lehet újraszinkronizálni.
   */
  get editingInvoiceHasWrongItemInvoice(): boolean {
    return this.editingInvoiceHistory.some((h) => h.hasWrongItemInvoice);
  }

  /** Betölti a megadott id-jú számlafejet a részletes szerkesztő számára */
  async loadEditingInvoice(id: number): Promise<void> {
    if (id <= 1) {
      this.editingInvoice = { ...IfaceSyncInvoiceHeadDTODefaultProps };
      this.editingInvoiceHistory = [];
      this.editingInvoiceIsLatest = true;
      return;
    }
    const p = await api.getInvoiceHeadById(id);
    this.editingInvoice = p ?? { ...IfaceSyncInvoiceHeadDTODefaultProps };
    await this.loadEditingInvoiceHistory();
  }

  /**
   * Betölti a szerkesztett rekord szinkron sorának összes köztes tábla sorát (interfész +
   * külső azonosító), csökkenő id szerint rendezve, és eldönti, hogy a szerkesztett rekord-e
   * a legutolsó (legnagyobb azonosítójú) sor.
   */
  private async loadEditingInvoiceHistory(): Promise<void> {
    this.editingInvoiceHistory = [];
    this.editingInvoiceIsLatest = true;
    const p = this.editingInvoice;
    if (p.id <= 1 || !p.extCode) {
      return;
    }
    const history = await api.getInvoiceHeadHistory(this.editingInvoiceIface, p.extCode) ?? [];
    this.editingInvoiceHistory = [...history].sort((a, b) => b.id - a.id);
    this.editingInvoiceIsLatest = history.every((h) => h.id <= p.id);
  }

  /** A szerkesztett számla interfésze — a DTO-ból, ha nincs, a kiválasztott interfész. */
  private get editingInvoiceIface(): number {
    return this.editingInvoice.interface > 1 ? this.editingInvoice.interface : this.selectedInterface;
  }

  /**
   * A részletes szerkesztőben szerkesztett rekord a megadott interfészen van-e. A felület ezzel
   * dönti el kontrollonként, hogy egy mező szerkeszthető-e — több interfészre a template-ben
   * fűzhető össze: `:disabled="store.isInterface(Iface.BillCity) || store.isInterface(Iface.Incadea)"`.
   * A backend csak előtölti a számolt értékeket, a szerkeszthetőségről nem nyilatkozik.
   */
  isInterface(iface: Iface): boolean {
    return this.editingInvoiceIface == iface;
  }

  /**
   * A szerkesztett számla interfészéhez tartozó fizetési mód mappelések betöltése. A választóban a
   * K2D megnevezés látszik, de a köztes táblába a mappelés külső kódja kerül vissza.
   */
  async loadEditingInvoicePayModeOptions(): Promise<void> {
    this.payModeOptions = await api.getDictionaryOptions(this.editingInvoiceIface, globalStore.tableName.PayMode) ?? [];
  }

  /**
   * A szerkesztett számla interfészéhez tartozó áfakulcs mappelések betöltése — a tétel szerkesztő
   * választója ebből épül, és a köztes táblába a mappelés külső kódja kerül vissza.
   */
  async loadEditingInvoiceTaxOptions(): Promise<void> {
    this.taxOptions = await api.getDictionaryOptions(this.editingInvoiceIface, globalStore.tableName.Tax) ?? [];
  }

  /**
   * A szinkron sor legutolsó köztes tábla sorának betöltése a részletes szerkesztőbe. Újraszinkronizálás
   * és kihagyás után ez tölti be a művelettel létrejött új sort, így a felhasználó a szerkesztőben marad.
   */
  async loadEditingInvoiceLatest(): Promise<void> {
    const extCode = this.editingInvoice.extCode;
    if (!extCode) return;

    const history = await api.getInvoiceHeadHistory(this.editingInvoiceIface, extCode) ?? [];
    const latest = [...history].sort((a, b) => b.id - a.id)[0];
    if (latest) {
      await this.loadEditingInvoice(latest.id);
    }
  }

  /** K2D pénznemek betöltése a pénznem választóhoz (a duplikált országonkénti pénznemek kiszűrésével). */
  async loadCurrencies(): Promise<void> {
    const countries = await api.getCurrencies() ?? [];
    const currencies = countries.map((c) => c.currency).filter((c) => !!c);
    this.currencies = [...new Set(currencies)].sort();
  }

  /**
   * A kiválasztott K2D partner külső kódja a szerkesztett számla interfészén — ez kerül a köztes
   * tábla partner kód mezőjébe. Ha a partnerhez nincs mappelés, üres stringgel tér vissza.
   */
  async getEditingInvoicePartnerExtCode(partnerId: number): Promise<string> {
    return await api.getPartnerExtCode(this.editingInvoiceIface, partnerId) ?? "";
  }

  /** A szerkesztett számla interfész típusa az irány alapján (OUT → kimenő, egyébként bejövő). */
  private get editingInvoiceIfaceType(): IfaceType {
    return this.editingInvoice.direction === "OUT" ? IfaceType.OutInvoice : IfaceType.InInvoice;
  }

  /**
   * A szerkesztett számla újraszinkronizálása — a backend a DTO-ból új köztes tábla fej sort
   * hoz létre (tételekkel együtt), majd az új fej azonosítójával meghívja az IfaceSync API-t.
   * A tételrácsban szerkesztett tételek `modifiedItems`-ként mennek a fejre csatolva.
   */
  async downSyncEditingInvoice(): Promise<void> {
    const payload = this.buildSelectedItemsPayload([this.editingInvoice])[0];
    await api.downSyncEditedInvoiceHead(payload, this.editingInvoiceIface, this.editingInvoiceIfaceType);
  }

  /**
   * A szerkesztett számla kihagyása a szinkron folyamatból — ugyanaz a backend végpont,
   * mint a kezdőfelület „Kihagyva" gombjánál, egyetlen tétellel (fej + tételek másolódnak).
   */
  async markEditingInvoiceSkipped(): Promise<void> {
    const payload = this.buildSelectedItemsPayload([this.editingInvoice]);
    await api.markSelectedItemsSkipped(payload, this.editingInvoiceIface, this.editingInvoiceIfaceType);
  }

  /**
   * A szerkesztett számla szinkron megjegyzésének mentése — csak a sync_remark és az
   * upddate/updtime/upduser mezők módosulnak, a usermodified flag NEM változik.
   */
  async saveEditingInvoiceRemark(): Promise<void> {
    await api.setSyncRemarkOnSelectedItems([this.editingInvoice.id], this.editingInvoice.syncRemark ?? "", this.editingInvoiceIfaceType);
  }

  // --- Toolbar action-ok — az API-t a store csomagolja, a view csak hív ---

  /**
   * Kijelölt elemek down-syncje. Számláknál a `_cachedItems`-ből
   * `userModified===true` tételek a fejre kerülnek (`modifiedItems`) — a backend ID
   * szerint overlay-eli őket a DB-beli eredeti tételekre.
   */
  async downSyncSelectedItems(): Promise<void> {
    const payload = this.buildSelectedItemsPayload(this.selectedItems);
    await api.downSyncSelectedItems(payload, this.selectedInterface, this.selectedInterfaceType);
  }

  /** Interfész down-sync a store `fromDate` / `toDate` intervallumára. */
  async downSyncInterface(): Promise<void> {
    await api.downSyncInterface(this.fromDate, this.toDate, this.selectedInterface, this.selectedInterfaceType);
  }

  /** Összeállított (egyedi szűrésű) lekérdezés indítása az aktuális interfészre. */
  async customDownSyncInterface(request: GeneralGetPurchaseInvoicesRequestDTO): Promise<void> {
    await api.customDownSyncInterface(request, this.selectedInterface, this.selectedInterfaceType);
  }

  /** Összeállított gépjármű lekérdezés / betöltés indítása az aktuális interfészre (CarRequestTool). */
  async customDownSyncCars(request: GeneralGetCarsRequestDTO): Promise<void> {
    await api.customDownSyncCars(request, this.selectedInterface, this.selectedInterfaceType);
  }

  /** Összeállított partner lekérdezés / betöltés indítása az aktuális interfészre (PartnerRequestTool). */
  async customDownSyncPartners(request: GeneralGetPartnersRequestDTO): Promise<void> {
    await api.customDownSyncPartners(request, this.selectedInterface, this.selectedInterfaceType);
  }

  /**
   * UpSync: a kijelölt köztes sorok rekordjainak újra betöltése a törzsből a köztes táblába (pl. a törzsadat
   * közben változott). A kijelölt sorok belső azonosítóiból (car / partner / invNo) { carIds }, { partnerIds } vagy
   * { invoiceIds } kérés készül, ami az "összeállított kérés" úton (rawRequest) változatlanul megy az IfaceSync API
   * load végpontjára. Partnernél/gépjárműnél a betöltés a korábbi, még nem felküldött sort kihagyottra állítja.
   * Verk kimenő számlánál (vezérrekord) a DLMInterfaceSync a GENO számlát elölről építi fel (partner, gépjárművek,
   * spnifcverkinvapi) és új, küldésre váró vezérrekordot hoz létre; a régi hibás sor megmarad.
   */
  async upSyncReloadSelectedItems(): Promise<void> {
    const internalIds = this.selectedItems
      .map((it: any) => {
        if (this.isVerkOutInvoice) return it.invNo;
        //Megasoft: kimenőnél és bejövőnél is a köztes sor ext_code-ja a kanri számla (hlkaninvoice.szam) - bejövőnél nincs GENI
        if (this.isMegasoftInvoice) return Number(it.extCode);
        return this.selectedInterfaceType === IfaceType.Car ? it.car : it.partner;
      })
      .filter((id: number) => typeof id === "number" && !isNaN(id) && id > 1)
      .filter((id: number, index: number, ids: number[]) => ids.indexOf(id) === index);
    const request = this.isVerkOutInvoice || this.isMegasoftInvoice ? { invoiceIds: internalIds }
      : this.selectedInterfaceType === IfaceType.Car ? { carIds: internalIds }
      : { partnerIds: internalIds };
    await api.upSyncReloadSelectedItems(request, this.selectedInterface, this.selectedInterfaceType);
  }

  /**
   * Kijelölt elemek megjelölése „Kihagyva" státusszal. Ugyanaz a payload-építés,
   * mint a `downSyncSelectedItems`-nél — a backend számláknál a tételeket is átmásolja.
   */
  async markSelectedItemsSkipped(): Promise<void> {
    const payload = this.buildSelectedItemsPayload(this.selectedItems);
    await api.markSelectedItemsSkipped(payload, this.selectedInterface, this.selectedInterfaceType);
  }

  /** Szinkron megjegyzés beállítása a kijelölt sorok `sync_remark` mezőjére. */
  async setSyncRemarkOnSelectedItems(remark: string): Promise<void> {
    const ids = this.selectedItems
      .map((it: any) => it.id)
      .filter((id: number) => typeof id === "number" && id > 0);
    await api.setSyncRemarkOnSelectedItems(ids, remark, this.selectedInterfaceType);
  }

  /**
   * Selected items → backend payload — számláknál a felhasználó által szerkesztett
   * tételek (`_cachedItems`-ből `userModified===true`) a fejre csatolva mennek.
   */
  private buildSelectedItemsPayload(items: any[]): any[] {
    return items.map((it: any) => {
      if (Array.isArray(it._cachedItems)) {
        return {
          ...it,
          modifiedItems: it._cachedItems.filter((ci: any) => ci?.userModified === true),
        };
      }
      return it;
    });
  }
}

const store = new InterfaceStore();
export default store;
